import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import {
  useCancelAbsence,
  useEndSession,
  useManualCheckIn,
  useMarkAbsent,
} from '@/hooks/coaching/useSessionControl'
import type { CoachingSessionStateView } from '@/types/coaching'
import { ConfirmDialog } from './ConfirmDialog'

interface Props {
  learnerId: number
  sessionState: CoachingSessionStateView
  extraActions?: ReactNode
}

function apiErrorCode(e: unknown): string | null {
  if (axios.isAxiosError(e)) return e.response?.data?.error?.code ?? null
  return null
}

export function SessionControls({ learnerId, sessionState, extraActions }: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const end = useEndSession(learnerId)
  const checkIn = useManualCheckIn(learnerId)
  const absent = useMarkAbsent(learnerId)
  const cancelAbsence = useCancelAbsence(learnerId)
  const [endOpen, setEndOpen] = useState(false)
  const [absentOpen, setAbsentOpen] = useState(false)
  const [cancelAbsenceOpen, setCancelAbsenceOpen] = useState(false)
  const [absentReason, setAbsentReason] = useState('')
  const [teacherComment, setTeacherComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  const absentDialogTitle = sessionState.attendanceOpen
    ? t('sessionControl.absentOpenTitle')
    : t('sessionControl.absentTitle')
  const absentDialogMessage = sessionState.attendanceOpen
    ? t('sessionControl.absentOpenMessage')
    : t('sessionControl.absentMessage')
  const absentDialogConfirm = sessionState.attendanceOpen
    ? t('sessionControl.absentOpenConfirm')
    : t('sessionControl.absentConfirm')
  const hasGrowthRecord = teacherComment.trim().length > 0
  const isAbsent = sessionState.attendanceStatus === 'ABSENT'

  const onMutationError = (e: unknown) => {
    const code = apiErrorCode(e)
    setError(code ? t(`common:apiError.${code}`, t('common:error.unknown')) : t('common:error.unknown'))
  }

  const handleEndConfirm = () => {
    end.mutate(teacherComment.trim() || undefined, {
      onSuccess: () => {
        setEndOpen(false)
        setTeacherComment('')
      },
      onError: (e) => {
        setEndOpen(false)
        const code = apiErrorCode(e)
        setError(code ? t(`common:apiError.${code}`, t('common:error.unknown')) : t('common:error.unknown'))
      },
    })
  }

  const handleAbsentConfirm = () => {
    absent.mutate(absentReason.trim() || undefined, {
      onSuccess: () => { setAbsentOpen(false); setAbsentReason('') },
      onError: (e) => { setAbsentOpen(false); onMutationError(e) },
    })
  }

  const handleCancelAbsenceConfirm = () => {
    cancelAbsence.mutate(undefined, {
      onSuccess: () => setCancelAbsenceOpen(false),
      onError: (e) => { setCancelAbsenceOpen(false); onMutationError(e) },
    })
  }

  return (
    <div className="flex flex-col items-end gap-2 shrink-0">
      {/* 원생 관리 버튼: [등원] [하원] [결석] */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setError(null); checkIn.mutate(undefined, { onError: onMutationError }) }}
          disabled={sessionState.attendanceOpen || isAbsent || checkIn.isPending}
          className="px-4 py-2 rounded-lg border border-lc-mint-900 text-lc-mint-900 text-sm font-semibold hover:bg-lc-mint-100 disabled:border-gray-200 disabled:text-gray-400"
        >
          {t('sessionControl.checkIn')}
        </button>
        <button
          type="button"
          onClick={() => { setError(null); setEndOpen(true) }}
          disabled={!sessionState.attendanceOpen || end.isPending}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400"
        >
          {t('sessionControl.checkOut')}
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null)
            if (isAbsent) setCancelAbsenceOpen(true)
            else setAbsentOpen(true)
          }}
          disabled={absent.isPending || cancelAbsence.isPending}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-400"
        >
          {isAbsent ? t('sessionControl.cancelAbsence') : t('sessionControl.absent')}
        </button>
        {extraActions}
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600">{error}</p>
      )}
      <ConfirmDialog
        open={endOpen}
        title={t('sessionControl.checkOutTitle')}
        message={t('sessionControl.checkOutMessage')}
        confirmLabel={hasGrowthRecord
          ? t('sessionControl.saveAndCheckOutConfirm')
          : t('sessionControl.checkOutConfirm')}
        cancelLabel={t('sessionControl.cancel')}
        pending={end.isPending}
        onCancel={() => {
          setEndOpen(false)
          setTeacherComment('')
        }}
        onConfirm={handleEndConfirm}
      >
        <label className="block text-sm font-semibold text-gray-800">
          {t('sessionControl.teacherCommentLabel')}
          <textarea
            value={teacherComment}
            onChange={(event) => setTeacherComment(event.target.value)}
            rows={3}
            placeholder={t('sessionControl.teacherCommentPlaceholder')}
            className="mt-2 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm font-normal text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <p className="mt-1.5 text-xs text-gray-500">{t('sessionControl.teacherCommentHelp')}</p>
      </ConfirmDialog>
      {absentOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setAbsentOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900 mb-2">{absentDialogTitle}</h2>
            <p className="text-sm text-gray-700 mb-3 whitespace-pre-line">{absentDialogMessage}</p>
            <textarea
              value={absentReason}
              onChange={(e) => setAbsentReason(e.target.value)}
              rows={2}
              placeholder={t('sessionControl.absentReasonPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setAbsentOpen(false)}
                disabled={absent.isPending}
              >
                {t('sessionControl.cancel')}
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300"
                onClick={handleAbsentConfirm}
                disabled={absent.isPending}
              >
                {absentDialogConfirm}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={cancelAbsenceOpen}
        title={t('sessionControl.cancelAbsenceTitle')}
        message={t('sessionControl.cancelAbsenceMessage')}
        confirmLabel={t('sessionControl.cancelAbsenceConfirm')}
        cancelLabel={t('sessionControl.cancel')}
        pending={cancelAbsence.isPending}
        tone="primary"
        onCancel={() => setCancelAbsenceOpen(false)}
        onConfirm={handleCancelAbsenceConfirm}
      />
    </div>
  )
}
